import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router';
import {
  fromParamString,
  type ParamType,
  type URLVal,
  updateParam,
} from 'src/components/utils/urlState';

export type StateConfig<T extends URLVal> = {
  defaultValue?: T;
  isMultiple?: boolean;
  paramType?: ParamType;
};

type StatesConfig<T extends URLVal = URLVal> = Record<string, StateConfig<T>>;

export type StateValues<T extends StatesConfig> = {
  [K in keyof T]: T[K]['defaultValue'];
};

type Options = {
  prefix?: string;
};

/**
 * A custom React hook that synchronizes multiple typed state values with the browser URL search parameters.
 *
 * It automatically parses and serializes URL parameters based on their defined type
 * (e.g., string, number, boolean, date, or tags), allowing complex UI state
 * (filters, pagination, sorting, etc.) to be reflected in and restored from the URL.
 *
 * @template T - Configuration type describing all supported URL parameters
 * @param config - An object mapping parameter names to configuration options:
 *   - `defaultValue` — the default value when the parameter is not present in the URL
 *   - `isMultiple` — whether the parameter can appear multiple times (array values)
 *   - `paramType` — the expected parameter type (`'string' | 'number' | 'boolean' | 'tags' | 'date'`)
 * @param opts - Additional options for URL handling.
 *   - `prefix` — Optional string to prepend to all parameter names in the URL.
 *
 * @returns A tuple `[values, setValues]`:
 *   - `values` — an object containing the current, typed values of all configured URL parameters
 *   - `setValues` — a function to partially update URL parameters while keeping others unchanged
 *
 * @example
 * ```ts
 * const [filters, setFilters] = useURLStates({
 *   search: { defaultValue: '', paramType: 'string' },
 *   page: { defaultValue: 1, paramType: 'number' },
 *   tags: { isMultiple: true, paramType: 'string' },
 * });
 *
 * console.log(filters.page); // -> number (e.g., 1)
 * setFilters({ page: 2, search: 'query' }); // -> URL becomes ?page=2&search=query
 * ```
 */
export function useURLStates<T extends StatesConfig>(
  config: T,
  opts?: Options,
): [StateValues<T>, (newValues: Partial<StateValues<T>>) => void] {
  const [searchParams, setSearchParams] = useSearchParams();

  const values = useMemo<StateValues<T>>(() => {
    const result = {} as StateValues<T>;
    for (const key in config) {
      const { defaultValue, isMultiple = false, paramType = 'string' } = config[key];
      const paramKey = opts?.prefix ? `${opts.prefix}${key}` : key;
      const raw = searchParams.get(paramKey);
      result[key] = fromParamString(paramType, isMultiple, raw, defaultValue);
    }
    return result;
  }, [searchParams, config, opts?.prefix]);

  // Synchronous on purpose: a controlled input bound to these values drops keystrokes otherwise.
  const setValues = useCallback(
    (newValues: Partial<StateValues<T>>) => {
      const params = new URLSearchParams(window.location.search);
      for (const key in newValues) {
        if (config[key]) {
          const { isMultiple = false, paramType = 'string' } = config[key];
          const value = newValues[key];
          const paramKey = opts?.prefix ? `${opts.prefix}${key}` : key;

          updateParam(params, paramKey, value, { paramType, isMultiple });
        }
      }
      setSearchParams(params, { replace: true });
    },
    [config, setSearchParams, opts?.prefix],
  );

  return [values, setValues];
}
